import * as d3 from 'd3';
import React from 'react';
import Draggable from 'react-draggable';
import { Box, Checkbox, FormControlLabel, IconButton, Paper, TableCell, TableHead, TableRow, TableSortLabel, Toolbar, Tooltip, Typography, TextField } from '@material-ui/core';
import _ from 'lodash';
import { controlType } from '../constants/common';
import moment from 'moment';
import { getFieldValueByFormat, getFormattedValues, getFormattedValuesForGraph, getFormattedHeaders } from './formatData';
import StreamLogo from '../public/img/common/LogoNavyCopy.jpg';
import {
  DateFormat,
  SECTIONS
} from '../constants/appConstants';
import store from '../store/appStore';
import jsPDF from 'jspdf';
import { getSSRSBaseUrl } from '../constants/api';
import PropTypes from 'prop-types';
import { ArrowDropDown, ArrowLeft } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';

function useQuery() {
  const windowUrl = window.location.search;
  const params = new URLSearchParams(windowUrl);
  return params;
}
function ConvertToCurrencyAbbreviation(number, decPlaces) {
  // 2 decimal places => 100, 3 => 1000, etc
  decPlaces = Math.pow(10, decPlaces);

  // Enumerate number abbreviations
  const abbrev = [' K', ' M', ' B', ' T'];

  // Go through the array backWards, so we do the largest first
  for (let i = abbrev.length - 1; i >= 0; i--) {
    // Convert array index to "1000", "1000000", etc
    const size = Math.pow(10, (i + 1) * 3);

    // If the number is bigger or equal do the abbreviation
    if (size <= number) {
      // Here, we multiply by decPlaces, round, and then divide by decPlaces.
      // This gives us nice rounding to a particular decimal place.
      number = Math.round((number * decPlaces) / size) / decPlaces;

      // Handle special case where we round up to the next abbreviation
      if (number === 1000 && i < abbrev.length - 1) {
        number = 1;
        i++;
      }

      // Add the letter for the abbreviation
      number += abbrev[i];

      // We are done... stop
      break;
    }
  }

  return number;
}
export { ConvertToCurrencyAbbreviation };

function getTipInfo(d, showEmployeeCount, chartConfig) {
  let tip = '';
  if (d?.data && d?.data?.length > 0) {
    const employeeCount = removeDuplicates([...new Set(d.data.map((x) => x.employeeId))]?.filter(x => x !== undefined && x !== null));
    if (employeeCount?.length > 0 && showEmployeeCount) {
      tip += '<span class="tipspan">Employee Count: ' + employeeCount.length + '</span><br/>';
    }
    const propertyId = removeDuplicates([...new Set(d.data.map((x) => x.propertyId))]?.filter(x => x !== undefined && x !== null));
    if (propertyId?.length > 0) {
      tip += '<span class="tipspan">Property Count: ' + propertyId.length + '</span><br/>';
    }
  }
  if (chartConfig && chartConfig.configuration && chartConfig.configuration.autoGenerate &&
    chartConfig.configuration.autoGenerate.dataColumn &&
    chartConfig.configuration.autoGenerate.dataColumn.length > 0) {
    chartConfig.configuration.autoGenerate.dataColumn.forEach(x => {
      if (d[x.key] !== undefined && d[x.key] !== null && d[x.key] !== '') {
        tip += '<span class="tipspan">' + x.title + ': ' + numberWithCommas(getFormattedValuesForGraph(d[x.key], (x.format || 'Text'))) + '</span><br/>';
      } else if (d.data !== undefined && d.data.length > 0 && d.data[0][x.key] !== undefined) {
        tip += '<span class="tipspan">' + x.title + ': ' + numberWithCommas(getFormattedValuesForGraph(d.data.length, (x.format || 'Text'))) + '</span><br/>';
      }
    }
    );
  }
  return tip;
}
export { getTipInfo };

function wrapText(text, width, hideXAxisLabel) {
  text.each(function () {
    const text = d3.select(this);
    const words = hideXAxisLabel === true ? [''] : text.text().split(/\s+/).reverse();
    let word;
    let line = [];
    let lineNumber = 0;
    const lineHeight = 1.1; // ems
    const y = text.attr('y');
    const dy = parseFloat(text.attr('dy'));
    let tspan = text
      .text(null)
      .append('tspan')
      .attr('x', 0)
      .attr('y', y)
      .attr('dy', dy + 'em');
    while ((word = words.pop())) {
      line.push(word);
      tspan.text(line.join(' '));
      if (tspan.node().getComputedTextLength() > width) {
        line.pop();
        tspan.text(line.join(' '));
        line = [word];
        tspan = text
          .append('tspan')
          .attr('x', 0)
          .attr('y', y)
          .attr('dy', ++lineNumber * lineHeight + dy + 'em')
          .text(word);
      }
    }
  });
}
export { wrapText };

function removeDuplicates(myArr, prop) {
  if (myArr === undefined) {
    return [];
  }

  if (prop === undefined) {
    return myArr.filter((obj, pos, arr) => {
      return arr.map((mapObj) => mapObj).indexOf(obj) === pos;
    });
  }
  return myArr.filter((obj, pos, arr) => {
    return arr.map((mapObj) => mapObj[prop]).indexOf(obj[prop]) === pos;
  });
}

const PaperComponent = (props) => {
  return (
    <Draggable
      handle="#responsive-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
};

const numberWithCommas = (num) => {
  const parts = num.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
  // return num?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

function applyFilterOnData(_dataToBeFiltered = [], selectedData, ignoreFilter = []) {
  let filteredData = [..._dataToBeFiltered];
  (selectedData || []).forEach((element) => {
    if (element.selected !== undefined && element.selected.length > 0) {
      if (ignoreFilter.find(x => x === element.key) === undefined) {
        if (element.key === 'employeeStatus') {
          if (element.selected.length > 0 && element.selected[0].employeeStatus === false) {
            return filteredData;
          } else {
            filteredData = getFilteredData(filteredData, element, 'employeeStatus');
          }
        } else if (element.key === 'onlyReimbursable') {
          if (element.selected.length > 0 && element.selected[0].onlyReimbursable === false) {
            return filteredData;
          } else {
            filteredData = getFilteredData(filteredData, element, 'isReimbursed');
          }
        } else if (element.key === 'showInactive') {
          return filteredData;
        } else if (element.key === 'isReimbursedExceed') {
          if (element.selected.length > 0 && element.selected[0].isReimbursedExceed === false) {
            return filteredData;
          } else {
            filteredData = getFilteredData(filteredData, element, 'isReimbursedExceed');
          }
        } else if (element.key === 'employeeName') {
          filteredData = getFilteredData(filteredData, element, 'preferredName');
        } else if (element.key === 'transitionTypeName') {
          filteredData = getFilteredData(filteredData, element, 'transitionType');
        } else if (element.key === 'serviceType') {
          filteredData = getFilteredData(filteredData, element, 'serviceTypeName');
        } else if (element.key === 'propertyTypeName') {
          filteredData = getFilteredData(filteredData, element, 'propertyType');
        } else if (element.key === 'propertySubTypeName') {
          filteredData = getFilteredData(filteredData, element, 'propertySubType');
        } else if (element.key === 'customerName') {
          filteredData = getFilteredData(filteredData, element, 'legalEntity');
        } else if (element.type === controlType.Year && element.selected[0][element.key] !== undefined && element.dontFilterOtherSource === undefined) {
          filteredData = filteredData.filter(y =>
            moment(moment(element.selected[0][element.key]).format('DD/MM/YYYY'), 'DD/MM/YYYY').year() === y[element.key]);
        } else if (element.filterOnKey !== undefined) {
          filteredData = getFilteredData(filteredData, element, element.filterOnKey);
        } else {
          filteredData = getFilteredData(filteredData, element, element.key);
        }
      }
    }
  });
  return filteredData;
}

// Get filtered data based on applied filters
function getFilteredData(filteredData, filter, fieldKey) {
  return filteredData.filter((x) => {
    return (filter.key in x || fieldKey in x) === true
      ? checkMatching(filter, x, fieldKey)
      : true;
  });
}

// Check matching of records based on field key
function checkMatching(element, x, fieldKey) {
  if (element.type === controlType.DatePicker) {
    if (element.operator || element.advanceFilterOperator) {
      let valueA = getFormattedValues(x[element.key], 'Date', true);
      let valueB = getFormattedValues(element.selected[0][fieldKey], 'Date', true);
      valueA = valueA === null ? -1 : (valueA !== '' ? new Date(valueA).getTime() : 0);
      valueB = valueB === null ? -1 : (valueB !== '' ? new Date(valueB).getTime() : 0);
      let res;
      if (element.advanceFilterOperator) {
        res = element.advanceFilterOperator(valueA, valueB, 'Date');
      } else {
        res = element.operator(valueA, valueB, 'Date');
      }
      if (res.result === true && element.And !== undefined) {
        valueA = getFormattedValues(x[element.And.key], 'Date', true);
        let valueB = getFormattedValues(element.selected[0][fieldKey], 'Date', true);
        valueA = valueA !== '' ? new Date(valueA).getTime() : 0;
        valueB = valueB !== '' ? new Date(valueB).getTime() : 0;
        res = element.And.operator(valueA, valueB, 'Date');
      }
      return res.result;
    }
    return (element.selected || []).find(
      (j) => j[element.key] ===
        (element.type === controlType.DatePicker
          ? moment(x[element.key]).format('MM/DD/YYYY')
          : x[element.key])
    );
  } else if (element.advanceFilterOperator) {
    const valueA = getFormattedValues(x[fieldKey], 'Text', true);
    let val = '';
    if (Array.isArray(element.selected)) {
      const data = [];
      element.selected.forEach(dx => {
        data.push(dx[element.key]);
      });
      val = data.join('|');
    } else {
      val = element?.selected[element.key];
    }
    const valueB = getFormattedValues(val, 'Text', true);
    const res = element.advanceFilterOperator(valueA, valueB);
    return res.result;
  } else if (element.isComaSeprated === true) {
    return (element.selected || []).find((j) =>
      ((x[element.key] || '').split('|') || []).find(
        (item) =>
          (item || '').toString().trim().toLowerCase() ===
          (j[element.key] || '').toString().trim().toLowerCase()
      ) ||
      ((x[fieldKey] || '').split('|') || []).find(
        (item) =>
          (item || '').toString().trim().toLowerCase() ===
          (j[element.key] || '').toString().trim().toLowerCase()
      )
    );
  } else if (element.checkString === true) {
    // if (x[element.key] !== null) {
    return (element.selected || []).find(
      (j) => (x[element.key] === null ? '' : x[element.key]).includes(j[element.key] || '') ||
        // j[element.key] ===
        // x[fieldKey] ||
        (x[fieldKey] === null ? '' : x[fieldKey]).includes((j[element.key] || ''))

    );
  } else {
    return (element.selected || []).find(
      (j) => j[element.key] ===
        x[element.key] ||
        j[element.key] ===
        x[fieldKey] ||
        (j[element.key] || '').toString() ===
        (x[fieldKey] || '').toString() ||
        j[element.key] === '' ||
        j[element.key] === undefined
    );
  }
}

// Update configurations of the column based on configurations received from backend
function UpdateColumnWithConfiguration(
  columns,
  configuration,
  hiddenConfig = undefined
) {
  const filteredCols = [];
  (columns || []).forEach((c, i) => {
    const config = (configuration || []).find(
      (x) =>
        _.camelCase(x.dbColumn).toString().toLowerCase().trim() ===
        _.camelCase(c.key).toString().toLowerCase().trim()
    );
    if (config !== undefined) {
      config.disabled = c.disabled !== undefined ? c.disabled : false;
      c = { ...c, ...config };
    }
    if (hiddenConfig !== undefined && c.hideWhen) {
      (c.hideWhen || []).forEach((hideField, j) => {
        const hiddenConfigItem = hiddenConfig.find(
          (y) => y.key === hideField.key
        );
        if (hiddenConfigItem) {
          if (hiddenConfigItem.value !== hideField.value) {
            filteredCols.push(c);
          }
        } else {
          filteredCols.push(c);
        }
      });
    } else {
      filteredCols.push(c);
    }
  });
  return filteredCols;
}

// Update field as per configurations
function UpdateFieldsWithConfiguration(fields, configuration, isEditable = true) {
  (fields || []).forEach((c, i) => {
    const config = (configuration || []).find(
      (x) =>
        _.camelCase(x.key).toLowerCase().trim() ===
        _.camelCase(c.dbColumn).toLowerCase().trim()
    );
    if (config !== undefined) {
      c = { ..._.cloneDeep(c), ..._.cloneDeep(config) };
    }
    if (c.dataFormat && c.dataFormat.trim() === 'Boolean') {
      c.type = controlType.Switch;
    }
    if (c.dataFormat && c.dataFormat.trim() === 'Date') {
      c.type = controlType.DatePicker;
    }
    if (c.dataFormat && c.dataFormat.trim() === 'FileInput') {
      c.type = controlType.FileInput;
    }
    if (c.dataFormat && c.dataFormat.trim() === 'File') {
      c.type = controlType.File;
    }
    if (c.dataFormat && c.dataFormat.trim() === 'PhoneNumber') {
      c.type = controlType.PhoneNumber;
    }
    if (c.dataFormat && c.dataFormat.trim() === 'Email') {
      c.type = controlType.Email;
    }
    if (c.dataFormat && c.dataFormat.trim() === 'ZipCode') {
      c.type = controlType.ZipCode;
    }
    fields[i] = c;
  });
  return fields;
}

// Apply the validations
function isGridFormValid(value, obj, formData) {
  // if (value === null) {
  //   if (formData.allocationEndDate !== null && obj.key === 'billingEndDate') {
  //     obj.isInErrorState = true;
  //     obj.errorMessage = obj.name + ' field is required';
  //     return obj;
  //   }
  // }
  if (obj.isVisible === true) {
    if (value === 'Invalid date') {
      obj.isInErrorState = true;
      obj.errorMessage = 'Please enter valid date in MM/DD/YYYY format';
      return obj;
    }

    const isRequiredField = checkRequiredValidation(value, obj, true);
    if (isRequiredField !== null) return isRequiredField;
    if (
      obj.dependentValidation !== undefined &&
      obj.dependentValidation.value === formData[obj.dependentValidation.key]
    ) {
      if (value === null || value === undefined || value === '') {
        obj.isInErrorState = true;
        obj.errorMessage = obj.name + ' field is required.';
        return obj;
      }
    }
    obj = checkRegValidation(value, obj);
    if (obj.isInErrorState) return obj;
  }
  return CompareValue(value, obj, formData);
}

// Compare values based on rules assigned into the configurations
function CompareValue(value, obj, formData) {
  let isValid = true;
  if (value === 0) {
    ((obj.rules || {}).And || []).forEach((rule) => {
      if (rule && isValid) {
        if (rule.key === '') {
          if (formData[obj.key] === 0) {
            obj.isInErrorState = true;
            obj.errorMessage = obj.name + ' should be greater than 0';
          }
        }
      }
    });
  }
  if (value != null && value !== '' && value !== undefined) {
    ((obj.rules || {}).And || []).forEach((rule) => {
      if (rule && isValid) {
        if (rule.key === '' && rule.operator !== undefined) {
          const res1 = rule.operator(value, rule.value);
          if (!res1.result) {
            obj.isInErrorState = true;
            obj.errorMessage = res1.message(obj.name, rule.displayName || '') + ' ' + rule.value;
            isValid = false;
            return obj;
          } else {
            obj.isInErrorState = false;
            obj.errorMessage = '';
            return obj;
          }
        } else if (
          formData[rule.key] === undefined ||
          formData[rule.key] === null ||
          formData[rule.key] === ''
        ) {
          return obj;
        }
        let valueA = null;
        let valueB = null;
        if (
          obj.dataFormat === 'Date' ||
          (obj.editConfig || {}).type === controlType.DatePicker
        ) {
          valueA = getFormattedValues(value, 'Date', true);
          valueB = getFormattedValues(formData[rule.key], 'Date', true);
          valueA = valueA !== '' ? new Date(valueA).getTime() : 0;
          valueB = valueB !== '' ? new Date(valueB).getTime() : 0;
        } else {
          valueA = value;
          valueB = formData[rule.key];
          if (valueA === 0 && valueB === 0) {
            return obj;
          }
        }
        const res = rule.operator(valueA, valueB);
        if (!res.result) {
          obj.isInErrorState = true;
          obj.errorMessage = res.message(obj.name, rule.displayName || '');
          isValid = false;
          return obj;
        } else {
          obj.isInErrorState = false;
          obj.errorMessage = '';
          return obj;
        }
      }
    });
    if (isValid) {
      ((obj.rules || {}).Or || []).forEach((rule) => {
        if (rule) {
          if (!rule.operator(value, formData[rule.key])) {
            obj.isInErrorState = true;
            obj.errorMessage = rule.message(obj.name, rule.displayName || '');
          } else {
            obj.isInErrorState = false;
            obj.errorMessage = '';
          }
        }
      });
    }
  } else {
    obj.isInErrorState = false;
    obj.errorMessage = '';
  }
  return obj;
}

// Check whether input is valid based on datatype of the field
function isInputValid(value, obj, formFields) {
  if (obj.isVisible === true) {
    if (
      obj.isRequired === false &&
      (value === null || value === undefined || value === '')
    ) {
      obj.isInErrorState = false;
      return obj;
    }
    const isRequiredField = checkRequiredValidation(value, obj);
    if (isRequiredField !== null) return isRequiredField;
    obj = checkRegValidation(value, obj);
  }
  return validateRules(obj, formFields);
}

// Apply RegEx validations
function checkRegValidation(value, obj) {
  let isValid = true;
  if (obj.isVisible === true) {
    if (obj.dbColumn === 'FeePercentage' && obj.dataFormat === 'DecimalPercent') {
      const numericInput = new RegExp(
        '^(\\d{1,3}|\\d{1,3}\\.\\d{1,2})$'
      );
      isValid = numericInput.test(value);
    } else if ((obj.dbColumn === 'FlatFee' || obj.dbColumn === 'MinimumFee' || obj.dbColumn === 'EngineerHourlyBillingRate' || obj.dbColumn === 'EngineerOvertimeHourlyBillingRate') && obj.dataFormat === 'MoneyNumber') {
      const numericInput = new RegExp(
        '^(\\d{1,6}|\\d{1,6}\\.\\d{1,2})$'
      );
      isValid = numericInput.test(value);
    } else if (
      obj.dataFormat === 'Number' ||
      obj.dataFormat === 'MoneyNumber' ||
      obj.dataFormat === 'Percent'
    ) {
      value = value.toString().replace(',', '');
      const numericInput = new RegExp(
        '^\\s*((\\d+(\\.\\d{0,2})?)|((\\d*(\\.\\d{1,2}))))\\s*$'
      ); // '/^[0-9\b]+$/');
      isValid = numericInput.test(value);
    } else if (obj.dataFormat === 'Text' || obj.dataFormat === 'MultiText') {
      const textInput = new RegExp('[a-zA-Z_][0-9a-zA-Z_]*');
      isValid = textInput.test(value);
    } else if (obj.dataFormat === 'NumericText') {
      const textInput = new RegExp('[0-9a-zA-Z_]');
      isValid = textInput.test(value);
    } else if (obj.dataFormat === 'Boolean') {
      if (
        value &&
        (value === '0' || value === '1' || value === true || value === false)
      ) {
        isValid = true;
      } else {
        isValid = false;
      }
    } else if (obj.dataFormat === 'Email') {
      const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      isValid = re.test((value || '').toString().toLowerCase());
    } else if (obj.dataFormat === 'PhoneNumber') {
      const textInput = /(?=(?:\D*\d){10,15}\D*$)/;
      isValid = textInput.test(value);
    } else if (obj.dataFormat === 'ZipCode') {
      if (!isNaN(value)) {
        if (value.length > 4 && value.length < 11) {
          isValid = true;
        } else {
          isValid = false;
        }
      } else {
        isValid = false;
      }
    } else if (obj.dataFormat === 'Date') {
      isValid = (obj.minDate ? new Date(value).getTime() >= new Date(obj.minDate).getTime() : true);
      if (isValid === false && obj.isRequired === false && obj.isInErrorState === false) {
        if (value === null) {
          isValid = true;
        }
      }
      obj.isInErrorState = isValid;
      if (isValid === false) {
        obj.isInErrorState = true;
        obj.errorMessage = 'Please enter valid date in field.';
        return obj;
      } else {
        obj.isInErrorState = false;
        obj.errorMessage = '';
      }
      isValid = (obj.maxDate ? new Date(value).getTime() <= new Date(obj.maxDate).getTime() : true);
      obj.isInErrorState = isValid;
      if (isValid === false) {
        obj.isInErrorState = true;
        obj.errorMessage = 'Please enter valid date in field.';
        return obj;
      } else {
        obj.isInErrorState = false;
        obj.errorMessage = '';
      }
      isValid = (value ? moment(value).format('MM/DD/YYYY') !== 'Invalid date' : true);
      obj.isInErrorState = isValid;
      if (isValid === false) {
        obj.isInErrorState = true;
        obj.errorMessage = 'Please enter valid date in field.';
      } else {
        obj.isInErrorState = false;
        obj.errorMessage = '';
      }
      isValid = obj?.editConfig?.shouldDisableDate !== undefined ? !(obj?.editConfig?.shouldDisableDate(new Date(value))) : true;
      obj.isInErrorState = isValid;
      if (isValid === false) {
        obj.isInErrorState = true;
        obj.errorMessage = 'Please enter valid date in field.';
      } else {
        obj.isInErrorState = false;
        obj.errorMessage = '';
      }
    } else if (obj.dataFormat === 'Year') {
      if (obj.minDate !== undefined) {
        isValid = (obj.minDate ? new Date(value).getTime() >= new Date(obj.minDate).getTime() : true);
        if (isValid === false && obj.isRequired === false && obj.isInErrorState === false) {
          if (value === null) {
            isValid = true;
          }
        }
        obj.isInErrorState = isValid;
        if (isValid === false) {
          obj.isInErrorState = true;
          obj.errorMessage = 'Please enter valid year in field.';
          return obj;
        } else {
          obj.isInErrorState = false;
          obj.errorMessage = '';
        }
      }
      if (obj.maxDate !== undefined) {
        isValid = (obj.maxDate ? new Date(value).getTime() <= new Date(obj.maxDate).getTime() : true);
        obj.isInErrorState = isValid;
        if (isValid === false) {
          obj.isInErrorState = true;
          obj.errorMessage = 'Please enter valid year in field.';
          return obj;
        } else {
          obj.isInErrorState = false;
          obj.errorMessage = '';
        }
      }

      isValid = (value ? moment(value).format('MM/DD/YYYY') !== 'Invalid date' : true);
      obj.isInErrorState = isValid;
      if (isValid === false) {
        obj.isInErrorState = true;
        obj.errorMessage = 'Please enter valid year in field.';
      } else {
        obj.isInErrorState = false;
        obj.errorMessage = '';
      }
    }

    if (isValid === false) {
      obj.isInErrorState = true;
      obj.errorMessage = 'Please enter valid data in field.';
    } else {
      obj.isInErrorState = false;
      obj.errorMessage = '';
    }
  }
  return obj;
}

// Check Required field validation
function checkRequiredValidation(value, obj, isCallFromGrid = false) {
  if (obj.isVisible === true) {
    if (
      obj.isRequired === true &&
      (value === null || value === undefined || (typeof (value) === 'string' && value?.trim() === '') || (typeof value === 'number' && value === 0) || ((obj.dataFormat === 'Number' || obj.dataFormat === 'Decimal') && Number(value) === 0))
    ) {
      obj.isInErrorState = true;
      if (obj.dataFormat === 'Date') {
        obj.errorMessage =
          (isCallFromGrid ? obj.name : obj.displayName) + ' field is required and should be in MM/DD/YYYY format.';
      } else {
        obj.errorMessage =
          (isCallFromGrid ? obj.name : obj.displayName) + ' field is required.';
      }
      return obj;
    }
  }
  return null;
}

// Apply and validate the rules assigned in the column configurations
function validateRules(field, formFields) {
  ((field.rules || {}).And || []).forEach((rule) => {
    if (rule) {
      const targetField =
        formFields.find(
          (x) => _.camelCase(x.dbColumn) === _.camelCase(rule.key)
        ) || {};
      if (
        targetField.isRequired === false &&
        (targetField.attributeValue === undefined ||
          targetField.attributeValue === null ||
          targetField.attributeValue === '')
      ) {
        return field;
      }
      let valueA = getFieldValueByFormat(field);
      let valueB = getFieldValueByFormat(targetField);
      valueA = valueA !== '' ? new Date(valueA).getTime() : 0;
      valueB = valueB !== '' ? new Date(valueB).getTime() : 0;
      const res = rule.operator(valueA, valueB);
      if (!res.result) {
        field.isInErrorState = true;
        field.errorMessage = res.message(
          field.displayName,
          targetField.displayName || ''
        );
        return field;
      }
    }
  });
  ((field.rules || {}).Or || []).forEach((rule) => {
    if (rule) {
      const targetField = formFields.find((x) => x.dbColumn === rule.key) || {};
      if (!rule.operator(field.attributeValue, targetField.attributeValue)) {
        field.isInErrorState = true;
        field.errorMessage = rule.message(
          field.displayName,
          targetField.displayName || ''
        );
      } else {
        field.isInErrorState = false;
        field.errorMessage = '';
      }
    }
  });
  return field;
}

// Get section attribute value based on key
function getSectionAttributeValueByKey(data, key, returnDefaultValue = true) {
  const value =
    (
      (data || []).find((x) => _.camelCase(x.dbColumn) === _.camelCase(key)) ||
      {}
    ).attributeValue || (returnDefaultValue ? '' : undefined);
  return value;
}

// Get section attribute based on key and value
function getSectionAttributesByKeyValue(data, key, value) {
  const filterData =
    _.cloneDeep((data || []).filter((x) => (x[_.camelCase(key)] || '').toString().toLowerCase() === (value || '').toString().toLowerCase()));

  return filterData;
}

// Get section attribute field value based on key value
function getSectionAttributeFieldValueByKeyValue(data, key, value, field) {
  const filterData =
    _.cloneDeep((data || []).find((x) => _.camelCase(x.dbColumn) === _.camelCase(key) &&
      (x.attributeValue || '').toString().toLowerCase() === (value || '').toString().toLowerCase())) || {};

  return filterData[_.camelCase(field)] || '';
}

// function getSectionAttributeFieldValueByKeyValues (data, filterkeys, field) {
//   const filterData =
//     _.cloneDeep((data || []).find((x) => _.camelCase(x.dbColumn) === _.camelCase(key) &&
//       (x.attributeValue || '').toString().toLowerCase() === (value || '').toString().toLowerCase())) || {};

//   return filterData[_.camelCase(field)] || '';
// }

// Remove section attribute using key-value
function RemoveSectionAttributeByKeyValue(data, key, value) {
  return (data || []).filter((x) => (x[_.camelCase(key)] || '').toString().toLowerCase() !==
    (value || '').toString().toLowerCase()) || [];
}

// Get section field based on key
function getSectionFieldByKey(data, key) {
  const field =
    (data || []).find((x) => _.camelCase(x.dbColumn) === _.camelCase(key)) ||
    {};
  return field;
}

// Get section attribute value by key
function setSectionAttributeValueByKey(
  data,
  key,
  value,
  setDefaultValue = true
) {
  const field =
    (data || []).find((x) => _.camelCase(x.dbColumn) === _.camelCase(key)) ||
    {};
  field.attributeValue = value || (setDefaultValue ? '' : undefined);
  return data;
}

// Set attribute value by attribute Key
function setAllSectionAttributeValueByKey(
  data,
  key,
  value,
  setDefaultValue = true
) {
  const field =
    (data || []).filter((x) => _.camelCase(x.dbColumn) === _.camelCase(key)) ||
    [];
  field.forEach((fi) => {
    fi.attributeValue = value || (setDefaultValue ? '' : undefined);
  });
  return data;
}

// Set section field attribute by key
function setSectionFieldAttributeByKey(data, key, attribute, value) {
  const field =
    (data || []).find((x) => _.camelCase(x.dbColumn) === _.camelCase(key)) ||
    {};
  field[attribute] = value;
  return field;
}

// Check if selected date falls under Previous Pay Cycle
function isDateInPreviousPayCycle(date) {
  let isPrevious = false;
  isPrevious = isDateInCurrentPayCycle(date);
  if (isPrevious === false && moment(date).format(DateFormat) < moment().format(DateFormat)) {
    isPrevious = true;
  } else {
    isPrevious = false;
  }
  return isPrevious;
}

// Check if selected date falls under Next Pay Period
function isDateInNextPayCycle(date) {
  let isNext = false;
  isNext = isDateInCurrentPayCycle(date);
  if (isNext === false && moment(date).format(DateFormat) > moment().format(DateFormat)) {
    isNext = true;
  } else {
    isNext = false;
  }
  return isNext;
}

// Check if selected date is in current pay period
function isDateInCurrentPayCycle(date) {
  let isValid = false;
  const currentMonthStartDate = moment().startOf('month').format(DateFormat);
  const currentMonthMidDate = moment(currentMonthStartDate, DateFormat).add(
    14,
    'days'
  );
  const currentMonthLastDate = moment().endOf('month').format(DateFormat);
  if (
    (moment(date).format(DateFormat) >= currentMonthStartDate &&
      moment(date).format(DateFormat) <= currentMonthMidDate.format(DateFormat) &&
      moment().format(DateFormat) >= currentMonthStartDate &&
      moment().format(DateFormat) <= currentMonthMidDate.format(DateFormat)) ||
    (moment(date).format(DateFormat) >= currentMonthMidDate.add(1, 'days').format(DateFormat) &&
      moment(date).format(DateFormat) <= currentMonthLastDate &&
      moment().format(DateFormat) >=
      currentMonthMidDate.add(1, 'days').format(DateFormat) &&
      moment().format(DateFormat) <= currentMonthLastDate)
  ) {
    isValid = true;
  }
  return isValid;
}

// Calculate pay period count between selected dates
function getNumberOfPayCycle(startDate, endDate) {
  const newStartDate = moment(startDate);
  const newEndDate = moment(endDate);
  const count = newEndDate.diff(newStartDate, 'days') + 1;
  // console.log(count/15);
  return parseInt(count / 15);
  // return parseInt(count / 13); // changed for 2nd pay cycle of Feb month
}

// Get valid End Date
function getValidEndDate(advanceFilterConfig) {
  let endDate;
  if (advanceFilterConfig.end !== undefined) {
    if (moment(advanceFilterConfig.end).diff(moment().format('MM/DD/YYYY'), 'days' > 1)) {
      advanceFilterConfig.end = moment().format('MM.DD/YYYY');
    }
    const count = moment(advanceFilterConfig.end).diff(moment(advanceFilterConfig.startDate), 'days');
    if (count > 1) {
      endDate = advanceFilterConfig.end;
    } else {
      endDate = advanceFilterConfig.endDate;
    }
  } else {
    endDate = advanceFilterConfig.endDate;
  }
  return endDate;
}

// Get current paycycle's start date
function getCurrentPayCycleStartDate() {
  const currentMonthStartDate = moment(
    moment().startOf('month').format(DateFormat)
  );
  const currentMonthMidDate = moment(currentMonthStartDate, DateFormat).add(
    14,
    'days'
  );
  if (moment().format(DateFormat) <= currentMonthMidDate.format(DateFormat)) {
    return currentMonthStartDate;
  } else {
    return currentMonthMidDate.add(1, 'days');
  }
}

// Get Max date based on selected date
function getMaxDate(formattedDate) {
  const count = getNumberOfPayCycle(formattedDate, moment().format(DateFormat));
  let futureEndDate;
  if (count > 2) {
    // futureEndDate = moment(getCurrentPayCycleEndDateByDate(moment(formattedDate).add(2, 'M').format('MM/DD/YYYY'))).endOf('month').format('MM/DD/YYYY');
    futureEndDate = moment(getCurrentPayCycleEndDateByDate(moment(formattedDate).add(1, 'M').subtract(1, 'days').format('MM/DD/YYYY'))).format('MM/DD/YYYY');
    // futureEndDate = moment(futureEndDate).subtract(1, 'days').format('MM/DD/YYYY');
  } else {
    // futureEndDate = moment().endOf('month').format('MM/DD/YYYY');
    futureEndDate = getCurrentPayCycleEndDateByDate(moment().format(DateFormat));
  }
  return futureEndDate;
}

// Get Start date of paycycle based on selected date
function getCurrentPayCycleStartDateByDate(date) {
  const currentMonthStartDate = moment(
    moment(date).startOf('month').format(DateFormat)
  );
  const currentMonthMidDate = moment(currentMonthStartDate, DateFormat).add(
    14,
    'days'
  );
  if (moment(date).format(DateFormat) <= currentMonthMidDate.format(DateFormat)) {
    return currentMonthStartDate;
  } else {
    return currentMonthMidDate.add(1, 'days');
  }
}

// Get Current paycycle's End Date
function getCurrentPayCycleEndDate() {
  const currentMonthStartDate = moment(
    moment().startOf('month').format(DateFormat)
  );
  const currentMonthMidDate = moment(currentMonthStartDate, DateFormat).add(
    14,
    'days'
  );
  if (moment().format(DateFormat) <= currentMonthMidDate.format(DateFormat)) {
    return currentMonthMidDate.add(1, 'days');
  } else {
    return moment(moment().endOf('month').format(DateFormat));
  }
}

// Get Pay Cycle End Date based on selectd date
function getCurrentPayCycleEndDateByDate(date) {
  const currentMonthStartDate = moment(
    moment(date).startOf('month').format(DateFormat)
  );
  const currentMonthMidDate = moment(currentMonthStartDate, DateFormat).add(
    14,
    'days'
  );
  if (moment(date).format(DateFormat) <= moment(currentMonthMidDate).format(DateFormat)) {
    // return currentMonthMidDate.add(1, 'days');
    return currentMonthMidDate;
  } else {
    return moment(moment(date).endOf('month').format(DateFormat));
  }
}

// Disable dates on calendar
function disableDatesForPayCycleStart1(date) {
  if (date.getDate() === 1 || date.getDate() === 16) {
    return false;
  }
  return true;
}

// Disable Dates on Calendar
function disableDatesForPayCycleEnd1(date) {
  const currentMonthEndDate = moment(
    moment(date).endOf('month').format('DD')
  );
  if (date.getDate() === 15 || date.getDate() == currentMonthEndDate._i) {
    return false;
  }
  return true;
}

// Get distinct records based on key
function getDistinctByProperty(data, key) {
  const distinct = [];
  (data || []).forEach((obj) => {
    if (
      distinct.find((x) => x[_.camelCase(key)] === obj[_.camelCase(key)]) ===
      undefined
    ) {
      distinct.push(_.cloneDeep(obj));
    }
  });
  return distinct;
}

// Check if user has Admin access
function IsAdmin() {
  const { user } = store.getState();
  if ((user || {}).userData) {
    if (
      (((user.userData || {}).groups || [])[0] || {})
        .accessGroupConfigurationId === 1
    ) {
      return true;
    }
  }
  return false;
}

// Check if user has Property Accounting Admin group access
export function IsPropertyAccountingAdmin() {
  const { user } = store.getState();
  if ((user || {}).userData) {
    if (
      (((user.userData || {}).groups || [])[0] || {})
        .accessGroupConfigurationId === 4
    ) {
      return true;
    }
  }
  return false;
}

// get User Configuration
function GetUserConfiguration() {
  const { user } = store.getState();
  return user.userData || {};
}

// Get Section Permission
function GetSectionPermission(parentSectionId, subSectionId = 0) {
  const { user } = store.getState();
  const permission = ((user.userData || {}).userPermissions || []).find(
    (x) =>
      x.parentSectionId === parentSectionId &&
      (x.subSectionId === subSectionId ||
        (subSectionId === 0 && x.subSectionId === parentSectionId))
  );

  if (permission !== undefined) {
    return permission;
  }
  return {};
}

// Check whether sensitive data access has given to User or not
function IsSensitiveDataAccess() {
  const { user } = store.getState();
  const isSensitiveData = ((user.userData || {}).globalAccessSectionPermissionByGroups || []).find((x) => x.accessType === 'Sensitive Data Access');
  return (isSensitiveData || {}).showData;
}

// Check if selected market is Datacenter market
function IsDataCenterMarket() {
  const { user } = store.getState();
  const userMarkets = ((user.userData || {}).userMarket || []).find((x) => x.marketId === 6 || x.marketId === 7);
  return userMarkets !== undefined;
}

// Check if user has Reimbursement amount access
function IsReimbursementAmountAccess() {
  const { user } = store.getState();
  const userMarkets = ((user.userData || {}).userMarket || []).find((x) => x.reimbursementAmountAccess);
  return userMarkets !== undefined;
}

// Check if user has access of Billing Accounting tab
function IsBillingAccountingTabVisible() {
  const { user } = store.getState();
  const userMarkets = ((user.userData || {}).userMarket || []).find((x) => x.billingAdjustmentAccess);
  return userMarkets !== undefined;
}

// Check whether Compliance Access is enabled to the logged-in user
function IsComplianceAccess() {
  const { user } = store.getState();
  const userMarkets = ((user.userData || {}).userMarket || []).find((x) => x.complianceAccess);
  return userMarkets !== undefined;
}

// Check whether auto run of pages option is enabled for the logged-in User
function IsAutoRunPagesEnabled() {
  const { user } = store.getState();
  const userMarkets = ((user.userData || {}).userMarket || []).find((x) => x.autoRunPages);
  return userMarkets !== undefined;
}

// Check whether user has access of Year End Bonus Estimation Review
function IsYearEndBonusEstimationReviewAccess(yearEndBonus, isEstimation = false) {
  // const { user } = store.getState();
  // const yearEndBonus = (user.userData || {}).yearEndComplianceStatus;
  if (isEstimation === true && yearEndBonus.estimationStatus === false && moment(yearEndBonus.allocationPeriodStartDate).format(DateFormat) <= moment().format(DateFormat) && moment(yearEndBonus.processEndDate).format(DateFormat) >= moment().format(DateFormat)) {
    return true;
  } else if (isEstimation === false && yearEndBonus.estimationStatus === true && yearEndBonus.reviewStatus === false && moment(yearEndBonus.processEndDate).format(DateFormat) >= moment().format(DateFormat)) {
    return true;
  }
  return false;
}

// Check whether user has access of Year End Bonus
function IsYearEndBonusAccess() {
  const { user } = store.getState();
  const userMarkets = ((user.userData || {}).userMarket || []).find((x) => x.yearEndBonusAccess);
  return userMarkets !== undefined;
}

// Get groups having read only access of service types
function GetReadOnlyServiceType() {
  const { user } = store.getState();
  return ((user.userData || {}).accessGroupServiceType || []).filter(x => x.isReadOnly === true);
}

// Check if section(page/report) is visible to user or not
function IsSectionVisible(parentSectionId, subSectionId = 0, action = null) {
  const permission = GetSectionPermission(parentSectionId, subSectionId);
  if (permission !== undefined) {
    if (action != null) {
      if (action === SECTIONS.ADD_EDIT) {
        return permission.accessTypeId > 1 && permission.accessTypeId <= 3;
      } else if (action === SECTIONS.VIEW) {
        return permission.accessTypeId === 1 || permission.accessTypeId === 3;
      }
    }
    if (permission.accessTypeId > 0 && permission.accessTypeId < 4) return true;
  }
  return false;
}

// Build a url to get widget data
function buildWidgetUrlWithParameter(url, arg) {
  if (arg && arg.length > 0) {
    arg.forEach((a) => {
      url = url?.replace('#' + a.key + '#', a.value);
    });
  }

  return url;
}

// Convert html file into pdf
function generatePDFFromHtml(title, element, fileName) {
  const unit = 'pt';
  const size = 'A4'; // Use A1, A2, A3 or A4
  const orientation = 'landscape'; // portrait or landscape

  const marginLeft = 40;
  const doc = new jsPDF(orientation, unit, size);
  doc.setFontSize(15);
  doc.text(title, 300, 40);
  const img = new Image();
  img.onload = function () {
    doc.addImage(StreamLogo, 'jpg', marginLeft, 40, 100, 200);
    doc.autoTable({ html: '#rptTable' });
    doc.save(fileName ? fileName + '.pdf' : title + '.pdf');
  };
  img.crossOrigin = '';
  img.src = StreamLogo;
};

// Handle order of widgets
function mapOrder(array, order, key) {
  const top = [];
  const rest = [];
  /* see https://developer.mozilla.org/de/docs/Web/JavaScript/Reference/Statements/for...of
    for "for of" loops, you might want to use a forEach() loop as "for of" isn't supported in many browsers as of now
    */
  order.forEach(x => {
    array.forEach(e => {
      if (e[key] === x) {
        top.push(e);
      } else if (e[key] !== x) {
        const obj = rest.find(y => y.key === e.key);
        const obj1 = order.find(y => y === e.key);
        if (obj === undefined && obj1 === undefined) {
          rest.push(e);
        }
      }
    });
  });

  return top.concat(rest);
};

// Generate url based on given parameters
function GenerateAbsoluteURL(data, group, reportName, type, isArray = false, sendObjectId = true) {
  type = type === 'Excel' ? 'EXCELOPENXML' : type;
  const url = getSSRSBaseUrl() + reportName + '&rs:Command=Render&rs:Format=' + type + '&rs:ClearSession=true';
  let baseUrl = '';
  data.push({ key: 'CloudOrgId', value: 1 });
  data.push({ key: 'UserEmail', value: (((store.getState() || {}).auth || {}).account || {}).username || '' });
  if (sendObjectId === true) {
    data.push({ key: 'ObjectId', value: (((((store.getState() || {}).user || {}).userData || {}).groups || [])[0] || {}).objectId || '' });
  }
  if (isArray) {
    if (group.length > 0) {
      group.forEach(x => {
        data.push({ key: 'GroupBy', value: x.ssrsReport });
      });
    }
  } else {
    if (group.length > 0) {
      const groupReport = group.map(x => x.ssrsReport);
      data.push({ key: 'GroupBy', value: groupReport.filter(x => x !== undefined).join('|') });
    }
  }

  data.forEach(x => {
    baseUrl += '&' + x.key + '=' + x.value;
  });
  return (url + baseUrl).toString();
}

// Clone deep object into another variable
function cloneDeep(inObject) {
  if (inObject === undefined) {
    return undefined;
  }
  // if (obj === null) return null;
  // const clone = Object.assign({}, obj);
  // Object.keys(clone).forEach(
  //   key =>
  //     (clone[key] =
  //       typeof obj[key] === 'object' ? cloneDeep(obj[key]) : obj[key])
  // );
  // if (Array.isArray(obj)) {
  //   clone.length = obj.length;
  //   return Array.from(clone);
  // }
  // return clone;
  let outObject, value, key;

  if (typeof inObject !== 'object' || inObject === null) {
    return inObject; // Return the value if inObject is not an object
  }

  // Create an array or object to hold the values
  outObject = Array.isArray(inObject) ? [] : {};

  for (key in inObject) {
    value = inObject[key];

    // Recursively (deep) copy for nested objects, including arrays
    outObject[key] = cloneDeep(value);
  }

  return outObject;
}

// Render filter field based on configuration
function renderFilterControl(config, props, headCells) {
  const format = config.filterConfig.controlType;
  const EditSource = props.EditSource;
  switch ((format || '').trim()) {
    case controlType.TextBox:
      return <TextField
        margin="normal"
        size="small"
        color="primary"
        width='100%'
        type={config.filterConfig.type}
        className="editable amount-text"
        onChange={(e) => props.onFilterChange(e.target.value, config.key, headCells)}
        value={props.updateData[config.key]}
      />;
    case controlType.DropDown:
      return <Autocomplete
        blurOnSelect
        id="tags-standard"
        width='100%'
        className="editable filter-width head-control"
        onChange={(e, val) => props.onFilterChange(val, config.key, headCells)}
        options={
          (EditSource.find((edx) => edx.key === config.filterConfig.source)?.data?.map((option) => option[config.filterConfig.key]))}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="standard"
            className="editable"
          />
        )}
        value={props.updateData[config.key]}
      />;
    case controlType.MultiSelectDropDown:
      return <Autocomplete
        blurOnSelect
        limitTags={1}
        id="tags-standard"
        className="editable filter-width"
        onChange={(e, val) => props.onFilterChange(val, config.key, headCells)}
        options={
          (EditSource.find((edx) => edx.key === config.filterConfig.source)?.data?.map((option) => option[config.filterConfig.key]))}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="standard"
            className="editable"
          />
        )}
        value={props.updateData[config.key]}
        multiple={true}
      />;
  }
}

// Create table header component
function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort, headCells, collapsableObj, extendibleArray, payPeriod, formattedPayPeriod, isSensitiveData, dynamicColumnCount, expand, expandAll, isMarket, suppressDetailsColumnCount, suppressDetails, hideExpandBar } = props;
  const createSortHandler = (property, format) => (event) => {
    onRequestSort(event, property, format);
  };
  const groupSequence = props.columnSequence;

  return (
    <TableHead className='background-table-header'>
      {payPeriod?.length > 0 && hideExpandBar !== true &&
        <TableRow>
          <TableCell className="st sticky" style={{ zIndex: '11' }}>
            <>
              {!isSensitiveData && isMarket
                ? <></>
                : <Tooltip title={expand ? 'Collapse All' : 'Expand All'}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={expand}
                        onChange={expandAll}
                        name="Expand All"
                        color="default"
                        size="small"
                        className={'white-checkbox'}
                      />
                    }
                    label={<p className={'expand-heading'}>{'Expand All'}</p>}
                  />
                </Tooltip>
              }
            </>
          </TableCell>
          {
            dynamicColumnCount.map((x, index) => (
              <TableCell key={'th' + index} className={'st sticky-' + x} style={{ zIndex: '11' }} >&nbsp;</TableCell>
            ))
          }
          {/* {
            suppressDetailsColumnCount?.map((x, index) => (
              <TableCell key={'th' + index} className={'st sticky-' + (dynamicColumnCount.length - 1 + x)} >&nbsp;</TableCell>
            ))
          } */}
          {/* <TableCell className="st sticky-1" style={{ zIndex: '11' }}>&nbsp;</TableCell>
            <TableCell className="st sticky-2" style={{ zIndex: '11' }}>&nbsp;</TableCell>
            <TableCell className="st sticky-3" style={{ zIndex: '11' }}>&nbsp;</TableCell>
            <TableCell className="st sticky-4" style={{ zIndex: '11' }}>&nbsp;</TableCell>
            <TableCell className="st sticky-5" style={{ zIndex: '11' }}>&nbsp;</TableCell>
            <TableCell className="st sticky-6" style={{ zIndex: '11' }}>&nbsp;</TableCell> */}
          {payPeriod.map((x, index) => (
            <TableCell className='right-border' key={index} colSpan={
              isMarket
                ? (isSensitiveData && x.isCollapsed === false ? 3 : 0)
                : ((!isSensitiveData && x.isCollapsed === false) ? (dynamicColumnCount.length - 5) : (isSensitiveData && x.isCollapsed === false) ? (dynamicColumnCount.length - 3) : 0)} align="center">
              <div>
                {!isSensitiveData && isMarket
                  ? <></>
                  : <IconButton
                    size="small"
                    className="rpt-header-icon"
                    onClick={() => props.onClickOnArrow(x.payPeriod, x.isCollapsed)}
                  >
                    {x.isCollapsed === false ? <ArrowLeft ></ArrowLeft> : <ArrowDropDown></ArrowDropDown>}
                  </IconButton>} {formattedPayPeriod !== null ? formattedPayPeriod : moment(x.payPeriod).format('MM-DD-YYYY')}</div>
            </TableCell>
          ))}
        </TableRow>}
      <TableRow>
        {groupSequence.map((col, index) => (
          <TableCell
            key={'th' + col.value + index}
            sortDirection={orderBy === col.id ? order : false}
            // className={(payPeriod !== undefined && payPeriod?.length > 0 ? (index === 0 ? 'st sticky' : 'st sticky-' + index) : '')}
            className={(index === 0 ? 'st sticky' : 'st sticky-' + index)}
          >
            {/* {col.key} */}
            <TableSortLabel
              active={orderBy === col.id}
              direction={orderBy === col.id ? order : 'asc'}
              onClick={createSortHandler(col.id, col.format)}
            >
              {col.type !== undefined ? getFormattedHeaders(col.key, col.type) : col.key}
            </TableSortLabel>
          </TableCell>
        ))}
        {
          suppressDetailsColumnCount?.map((x, index) => (
            <TableCell key={'th' + index} className={'st sticky-' + (groupSequence.length - 1 + x)} >&nbsp;</TableCell>
          ))
        }
        {headCells?.map((headCell) => (
          <TableCell
            width={headCell.width || 'auto'}
            key={headCell.id}
            //     align={headCell.numeric ? 'right' : 'left'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <>
              {
                headCell.isSort === false
                  ? <span className={headCell.className === undefined
                    ? 'number-text-report'
                    : headCell.className}>
                    <span
                      className={headCell.className === undefined
                        ? 'number-text'
                        : ''}>
                      {
                        (suppressDetails === true && headCell.hideColumn === true)
                          ? ''
                          : headCell.type !== undefined
                            ? getFormattedHeaders(headCell.label, headCell.type)
                            : headCell.label
                      }
                    </span>
                  </span>

                  : <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : 'asc'}
                    onClick={createSortHandler(headCell.id, headCell.format)}
                  >
                    {headCell.type !== undefined ? getFormattedHeaders(headCell.label, headCell.type) : headCell.label}
                  </TableSortLabel>
              }
            </>
          </TableCell>
        ))}

        {payPeriod?.map((_x) => (
          <>
            {_x.isCollapsed === false &&
              <>
                {extendibleArray.map((obj, index) => (
                  <TableCell key={obj.key + index}>
                    <span
                      className={obj.className === undefined ? 'number-text-report' : obj.className}
                    >
                      <span className={(obj.className === undefined ? 'number-text' : '')}
                      >
                        {(obj.hideColumn === true && suppressDetails === true) ? '' : obj.name}
                      </span>
                    </span>
                  </TableCell>
                ))}
                {/* <TableCell><span className='default-text'>{'Allocation Percent'}</span> </TableCell>
                    <TableCell> <span className='default-text'>{'Burden Rate Percent'}</span></TableCell>
                    <TableCell><span className='default-text'>{'Billing Percent'}</span></TableCell>
                    <TableCell><span className='default-text'>{'Flat Rate'}</span></TableCell> */}
              </>
            }
            {/* <TableCell className='right-border'>{getFormattedHeaders('Reimbursement Cost', 'Number')}</TableCell> */}
            <TableCell className='right-border'> <span className={extendibleArray.length > 1 ? 'report-percent-text' : 'number-text'}><span className='number-text table-header-text'>{collapsableObj.name}</span></span></TableCell>
          </>
        ))}
      </TableRow>
      {
        headCells.find(x => x.filterConfig !== undefined) !== undefined && <TableRow>
          {headCells?.map((headCell) => (
            <TableCell
              width={headCell.width || ''}
              key={headCell.id}
            >
              <>
                {
                  headCell.filterConfig !== undefined && renderFilterControl(headCell, props, headCells)
                }
              </>
            </TableCell>
          ))}
        </TableRow>
      }
    </TableHead >
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
  columnSequence: PropTypes.array,
  conditionalHeadCells: PropTypes.array,
  headCells: PropTypes.array,
  dynamicColumn: PropTypes.array,
  extendibleArray: PropTypes.array,
  collapsableObj: PropTypes.object,
  payPeriod: PropTypes.array,
  dynamicColumnCount: PropTypes.array,
  expand: PropTypes.bool,
  expandAll: PropTypes.func.isRequired

};

// function orderBy(data, orderBy, order) {
//   return _.orderBy(data || [], [data => ((data[orderBy]) !== null && data[orderBy] !== undefined && isNaN(data[orderBy]) !== false)
//     ? (moment(data[orderBy]).format('MM/DD/YYYY') !== 'Invalid date'
//       ? (new Date(data[orderBy])).getTime()
//       : (data[orderBy]).toString().toLowerCase())
//     : data[orderBy]], [order]);
// }

// Handle sorting
function orderBy(data, orderBy, order) {
  // return _.orderBy(data || [], [orderBy], [order]);
  const notNullValue = (data || []).find(x => x[orderBy] !== undefined && x[orderBy] !== null)?.[orderBy];
  if (notNullValue === undefined || notNullValue === null) {
    // consider it as string
    return _.orderBy(data || [], [orderBy], [order]);
  } else if (!isNaN(notNullValue) && typeof (notNullValue) === 'number') {
    // number
    return _.orderBy(data || [], [orderBy], [order]);
  } else if (moment(notNullValue).format('MM/DD/YYYY') !== 'Invalid date' && notNullValue?.includes('/')) { // && !isNaN(notNullValue)) {
    // date
    return _.orderBy(data || [], [data => (new Date(data[orderBy])).getTime()], [order]);
  } else {
    // string
    return _.orderBy(data || [], [data => ((data[orderBy]) || '').toString().toLowerCase()], [order]);
  }
}

// Handle sorting based on multiple selected fields
function orderByMultiple(data, orderBy, order, orderByMultiple = false) {
  return orderByMultiple
    ? _.orderBy(data || [], orderBy, order)
    : _.orderBy(data || [], [data => ((data[orderBy]) || '').toString().toLowerCase()], [order]);
}

// Remove null values from given array
function removeNullValuesFromArray(arr) {
  if (arr === undefined || arr === null || arr === []) {
    return arr;
  }
  const filtered = [];
  arr.forEach((x) => {
    if (x !== undefined && x !== null && x !== '' && x !== []) {
      filtered.push(x);
    }
  });

  return filtered;
}

// Get previous pay cycle
function getPreviousPayCycle() {
  let currentDate = moment().format('MM/DD/YYYY');
  const dayNum = currentDate.split('/')[1];
  if (parseInt(dayNum) === 31) {
    currentDate = moment(currentDate).subtract(16, 'days').format('MM/DD/YYYY');
  } else {
    currentDate = moment(currentDate).subtract(15, 'days').format('MM/DD/YYYY');
  }
  const startDate = moment(getCurrentPayCycleStartDateByDate(currentDate)).format('MM/DD/YYYY');
  const endDate = moment(getCurrentPayCycleEndDateByDate(currentDate)).format('MM/DD/YYYY');
  return { previousPayCycleStartDate: startDate, previousPayCycleEndDate: endDate };
}

// Get current pay cycle
function getCurrentPayCycle() {
  const date = [];
  const currentMonthStartDate = moment()
    .startOf('month')
    .format('MM/DD/YYYY');

  const currentMonthMidDate = moment(currentMonthStartDate, 'MM/DD/YYYY').add(
    14,
    'days'
  );
  const currentMonthLastDate = moment().endOf('month').format('MM/DD/YYYY');
  if (
    moment().format('MM/DD/YYYY') >= currentMonthStartDate &&
    moment().format('MM/DD/YYYY') <= currentMonthMidDate.format('MM/DD/YYYY')
  ) {
    date.push(currentMonthStartDate);
    date.unshift(currentMonthMidDate.format('MM/DD/YYYY'));
  } else {
    date.push(currentMonthLastDate);
    const currentMonthMidDate1 = moment(currentMonthMidDate, 'MM/DD/YYYY').add(
      1,
      'days'
    );
    date.push(currentMonthMidDate1.format('MM/DD/YYYY'));
  }
  return date;
}

// Check the hidden property of the field
function isHidden(row, col) {
  let isValid = true;
  if (col.editConfig.hideWhen !== undefined && col.editConfig.hideWhen.length > 0) {
    col.editConfig.hideWhen.forEach(x => {
      isValid = row[x.key] !== x.value;
      if (!isValid) return false;
    });
  } else if (col.editConfig.isHidden !== undefined) { return !col.editConfig.isHidden; }
  return isValid;
}

// Check display property of the field
function isDisplay(row, col) {
  let isValid = true;
  if (col.editConfig.showWhen !== undefined && col.editConfig.showWhen.length > 0) {
    const data = col.editConfig.showWhen.find(x => parseInt(row[x.key]) === x.value);
    isValid = data !== undefined;
  }
  return isValid;
}

// Format number upto 2 decimal places value
function with2DecimalsPoints(value, type) {
  try {
    let withDecimal = null;
    if (type === 'Percent') {
      withDecimal = value.toString().match(/^-?\d*(?:\.\d{0,2})?/)[0];
    } else {
      withDecimal = value.toString().match(/^-?\d*(?:\.\d{0,4})?/)[0];
    }
    return withDecimal;
  } catch {
    return value;
  }
}

// Get rounded decimal value upto 2 decimal places
function getRoundedValueUptoTwoDecimal(value) {
  try {
    return (Math.round((value + Number.EPSILON) * 100) / 100);
  } catch {
    return value;
  }
}

// Check validations on Year End Bonus page
export function checkYearEndBonusValidations(col, x, value) {
  let isValid = true;
  if (x.isEditable) {
    if (col.isRequired === true) {
      if (x[col.key] <= 0 || x[col.key] === undefined || x[col.key] === '' || x[col.key] === null) {
        x[col.key + 'IsInErrorState'] = true;
        x[col.key + 'ErrorMessage'] = 'Required.';
        isValid = false;
      } else {
        x[col.key + 'IsInErrorState'] = false;
        x[col.key + 'ErrorMessage'] = '';
      }
    } else if (col.rules) {
      ((col.rules || {}).And || []).forEach((rule) => {
        if (rule) {
          if (rule.key === '' && rule.operator !== undefined) {
            const res1 = rule.operator(value, rule.value);
            if (!res1.result) {
              x[col.key + 'IsInErrorState'] = true;
              x[col.key + 'ErrorMessage'] = 'Invalid.';
              isValid = false;
            } else {
              x[col.key + 'IsInErrorState'] = false;
              x[col.key + 'ErrorMessage'] = '';
            }
          }
        }
      });
    }
  }
  return { x, isValid };
}

// Build object based on grouping
function BuildObject(reportData, originalReportData, fieldName, data, isSet = true, fieldNameValue) {
  if (data.GroupId2 === undefined) {
    const index = reportData.findIndex(x => x[fieldName] === data[fieldName]);
    const clonedData = cloneDeep(originalReportData);
    reportData = cloneDeep(reportData);
    if (isSet === true) {
      data.isExpand = !data.isExpand;
    }
    originalReportData = clonedData.map(x => {
      if (x[fieldName] === data[fieldName] && x.IsChild === 0 && isSet === true) {
        x.isExpand = !x.isExpand;
      }
      return x;
    });
    reportData = reportData.map(x => {
      if (x[fieldName] === data[fieldName] && x.IsChild === 0 && isSet === true) {
        x.isExpand = !x.isExpand;
      } return x;
    });
    if (data.isExpand === true) {
      const filteredData = clonedData.filter(x => x[fieldName] === data[fieldName] && x.IsChild === 1);
      filteredData.forEach((data, i) => {
        if (reportData.find(y => y[fieldNameValue] === data[fieldNameValue] && y[fieldName] === data[fieldName]) === undefined) {
          reportData.splice((index + i + 1), 0, data);
        }
      });
    } else {
      reportData = reportData.filter(x => x.IsChild === 0 || (x[fieldName] !== data[fieldName] && x.IsChild === 1));
    }
    return { reportData, originalReportData };
  } else {
    const index = reportData.findIndex(x => x[fieldName] === data[fieldName]);
    const clonedData = cloneDeep(originalReportData);
    reportData = cloneDeep(reportData);
    if (isSet === true) {
      data.isExpand = !data.isExpand;
    }
    originalReportData = clonedData.map(x => {
      if (data.GroupId2 !== undefined) {
        if (data.IsChild === 0) {
          if (x[fieldName] === data[fieldName] && x.IsChild === data.IsChild && isSet === true) {
            x.isExpand = data.isExpand;
          }
        } else if (data.IsChild === 1) {
          if (x[fieldName] === data[fieldName] && x.GroupId2 === data.GroupId2 && x.IsChild >= data.IsChild && isSet === true) {
            x.isExpand = data.isExpand;
          }
        }
      } else {
        if (x[fieldName] === data[fieldName] && x.IsChild > data.IsChild && isSet === true) {
          x.isExpand = data.isExpand;
        }
      }
      return x;
    });
    reportData = reportData.map(x => {
      if (data.GroupId2 !== undefined) {
        if (data.IsChild === 0) {
          if (x[fieldName] === data[fieldName] && x.IsChild === data.IsChild && isSet === true) {
            x.isExpand = data.isExpand;
          }
        } else if (data.IsChild === 1) {
          if (x[fieldName] === data[fieldName] && x.GroupId2 === data.GroupId2 && x.IsChild >= data.IsChild && isSet === true) {
            x.isExpand = data.isExpand;
          }
        }
      } else {
        if (x[fieldName] === data[fieldName] && x.IsChild > data.IsChild && isSet === true) {
          x.isExpand = data.isExpand;
        }
      } return x;
    });
    if (data.isExpand === true) {
      let filteredData = clonedData.filter(x => x[fieldName] === data[fieldName] && x.IsChild === 1);
      // filteredData = _.orderBy(filteredData, 'IsChild', 'asc');
      let indexCounter = index;
      filteredData.forEach((data, i) => {
        if (reportData.find(y => y[fieldNameValue] === data[fieldNameValue] && y[fieldName] === data[fieldName]) === undefined) {
          indexCounter = (indexCounter + 1);
          reportData.splice(indexCounter, 0, data);

          if (data.GroupId2 !== undefined) {
            const groupId2 = ((data.GroupId2 || '').split('|')[1]) || '';
            if (groupId2 !== '') {
              filteredData = originalReportData.filter(x => x[fieldName] === data[fieldName] && x.IsChild === 2 && x.isExpand === true && (x.GroupId2 || '').split('|')[1] === groupId2);
              // filteredData = _.orderBy(filteredData, 'IsChild', 'asc');
              filteredData.forEach((data, i) => {
                if (reportData.find(y => y[fieldNameValue] === data[fieldNameValue] && y[fieldName] === data[fieldName] && y.GroupId2 === data.GroupId2) === undefined) {
                  indexCounter = (indexCounter + 1);
                  reportData.splice(indexCounter, 0, data);
                }
              });
            }
          }
        }
      });
    } else if (data.isExpand === false && data.IsChild === 1) {
      reportData = reportData.filter(x => x.IsChild === 0 || (x.isExpand === true && x.IsChild < 2));
    } else {
      reportData = reportData.filter(x => (x.IsChild === 0 || x.IsChild === data.IsChild) || (x[fieldName] !== data[fieldName] && x.IsChild === 2));
    }
    return { reportData, originalReportData };
  }
}

// Handle sorting of array
function SortArray(data, originalData, order, orderBy, fieldName, buildProperty = 'PropertyId') {
  // let sortedData = _.orderBy((data || []).filter(x => x.IsChild === 0), [data => ((data[orderBy]) !== null && data[orderBy] !== undefined && isNaN(data[orderBy]) !== false)
  //   ? (moment(data[orderBy]).format('MM/DD/YYYY') !== 'Invalid date'
  //     ? (new Date(data[orderBy])).getTime()
  //     : (data[orderBy]).toString().toLowerCase())
  //   : data[orderBy]], [order]);

  let sortedData = _.orderBy((data || []).filter(x => x.IsChild === 0), [orderBy], [order]);
  const notNullValue = ((data || []).filter(x => x.IsChild === 0) || []).find(x => x[orderBy] !== undefined && x[orderBy] !== null)?.[orderBy];
  if (notNullValue === undefined || notNullValue === null) {
    // consider it as string
    sortedData = _.orderBy((data || []).filter(x => x.IsChild === 0) || [], [orderBy], [order]);
  } else if (!isNaN(notNullValue) && typeof (notNullValue) === 'number') {
    // number
    sortedData = _.orderBy((data || []).filter(x => x.IsChild === 0) || [], [orderBy], [order]);
  } else if (moment(notNullValue).format('MM/DD/YYYY') !== 'Invalid date' && notNullValue?.includes('/')) { // && !isNaN(notNullValue)) {
    // date
    sortedData = _.orderBy((data || []).filter(x => x.IsChild === 0) || [], [data => (new Date(data[orderBy])).getTime()], [order]);
  } else {
    // string
    sortedData = _.orderBy((data || []).filter(x => x.IsChild === 0) || [], [data => ((data[orderBy]) || '').toString().toLowerCase()], [order]);
  }

  const filteredData = cloneDeep(sortedData);
  filteredData.map(x => {
    if (x.isExpand === true) {
      const obj = BuildObject(sortedData, originalData, buildProperty, x, false, fieldName);
      sortedData = obj.reportData;
      originalData = obj.originalReportData;
    }
    return x;
  });
  return sortedData;
}

// Handle expand all event. It will expand all groups to show detailed data.
function ExpandAll(originalReportData, reportData, value, showDetails = false, fieldName1, buildProperty = 'PropertyId') {
  let filterdOriginalReportData = cloneDeep(originalReportData);
  let filterdReportData = cloneDeep(reportData);

  if (showDetails === true) {
    filterdOriginalReportData.map(x => {
      x.isExpand = value;
      return x;
    });
    filterdReportData.map(x => {
      x.isExpand = value;
      return x;
    });
    filterdReportData.map(x => {
      const obj = BuildObject(filterdReportData, filterdOriginalReportData, buildProperty, x, false, fieldName1);
      filterdReportData = obj.reportData;
      filterdOriginalReportData = obj.originalReportData;
      return x;
    });
  }

  return { filterdReportData, filterdOriginalReportData };
}

// Returns last 3 pay periods 
function getLast3PayPeriodData() {
  const currentMonthStartDate = moment(
    moment().startOf('month').format(DateFormat)
  );
  let payPeriodEndDate;
  let payPeriodStartDate;
  if (isDateInCurrentPayCycle(currentMonthStartDate)) {
    payPeriodEndDate = moment(currentMonthStartDate).subtract(1, 'days').format(DateFormat);
  } else if (isDateInPreviousPayCycle(currentMonthStartDate)) {
    payPeriodEndDate = moment(currentMonthStartDate, DateFormat).add(
      14,
      'days'
    );
  }
  payPeriodStartDate = moment(payPeriodEndDate).subtract(1, 'Months').subtract(1, 'days').format(DateFormat);
  payPeriodStartDate = moment(getCurrentPayCycleStartDateByDate(payPeriodStartDate)).format(DateFormat);
  const date = [];
  date.push(payPeriodStartDate);
  date.push(payPeriodEndDate);
  return date;
}


export {
  getLast3PayPeriodData,
  BuildObject,
  SortArray,
  ExpandAll,
  with2DecimalsPoints,
  getRoundedValueUptoTwoDecimal,
  cloneDeep,
  GenerateAbsoluteURL,
  generatePDFFromHtml,
  removeDuplicates,
  PaperComponent,
  numberWithCommas,
  applyFilterOnData,
  useQuery,
  UpdateColumnWithConfiguration,
  UpdateFieldsWithConfiguration,
  isInputValid,
  getSectionAttributeValueByKey,
  isGridFormValid,
  setSectionAttributeValueByKey,
  getSectionFieldByKey,
  setSectionFieldAttributeByKey,
  isDateInCurrentPayCycle,
  isDateInPreviousPayCycle,
  isDateInNextPayCycle,
  getCurrentPayCycleStartDate,
  getDistinctByProperty,
  setAllSectionAttributeValueByKey,
  getCurrentPayCycleEndDate,
  buildWidgetUrlWithParameter,
  RemoveSectionAttributeByKeyValue,
  getSectionAttributesByKeyValue,
  getSectionAttributeFieldValueByKeyValue,
  IsAdmin,
  IsSectionVisible,
  GetUserConfiguration,
  getCurrentPayCycleStartDateByDate,
  getCurrentPayCycleEndDateByDate,
  mapOrder,
  disableDatesForPayCycleStart1,
  disableDatesForPayCycleEnd1,
  getNumberOfPayCycle,
  getValidEndDate,
  IsSensitiveDataAccess,
  EnhancedTableHead,
  getMaxDate,
  orderBy,
  removeNullValuesFromArray,
  getCurrentPayCycle,
  IsBillingAccountingTabVisible,
  IsReimbursementAmountAccess,
  getPreviousPayCycle,
  isHidden,
  isDisplay,
  GetReadOnlyServiceType,
  IsComplianceAccess,
  IsDataCenterMarket,
  IsYearEndBonusAccess,
  IsYearEndBonusEstimationReviewAccess,
  orderByMultiple,
  IsAutoRunPagesEnabled
};
